/**
 * FIXED FEE FORM STATE MANAGEMENT
 *
 * Composable for tracking unsaved changes in fixed fee forms.
 * Follows the same pattern as time entry form state management.
 *
 * KEY FEATURES:
 * 1. Tracks form changes by comparing current state vs original
 * 2. Provides confirmation dialog logic for unsaved changes
 * 3. Supports both new and existing fixed fee scenarios
 */
import { computed } from 'vue';
import { isEqual, cloneDeep } from 'lodash';

/**
 * Creates a simplified form state for comparison
 * @param {Object} charge - The charge object
 * @param {Object} dates - The dates object
 * @param {Object} formData - Additional form data
 * @param {Object} recurrence - Recurrence settings
 * @returns {Object} Simplified form state
 */
const getSimplifiedFormState = (charge, dates, formData, recurrence) => {
  return {
    // Basic fields
    amount: charge.amount,
    description: charge.description?.trim() || '',
    label: charge.label?.trim() || '',
    chargeType: charge.chargeType,
    customFlag: charge.customFlag,
    account: charge.account?.id || charge.account || null,
    // Matter/Client
    matterId: charge.cm?.matter?.id || charge.matter?.id || null,
    clientId: charge.cm?.client?.id || charge.client?.id || null,
    // Limit
    limitHours: charge.limit?.hours || null,
    limitRateAmount: charge.limit?.rate?.amount || null,
    // Codes
    activityCodeId: charge.codes?.activity?.id || null,
    taskCodeId: charge.codes?.task?.id || null,
    // Dates - use toNetworkDate() to avoid UTC timezone shift issues
    startDate: dates?.start ? new Date(dates.start).toNetworkDate() : null,
    endDate: dates?.end ? new Date(dates.end).toNetworkDate() : null,
    // Recurrence
    recurrenceId: recurrence?.id || null,
    count: formData?.count || 0,
    includeDate: formData?.includeDate || false,
    // Allocations (simplified comparison)
    allocationsHash: JSON.stringify((formData?.allocations || []).map(a => ({
      accountId: a.account?.id || a.accountId,
      amount: a.amount,
      percentage: a.percentage
    }))),
    // Completion state
    completed: charge.completed || false,
    // QuickBooks mapping
    accountingServiceId: charge.accounting?.service?.id || null
  };
};

/**
 * Composable for managing fixed fee form state and unsaved changes detection
 * @param {Object} options - Configuration options
 * @param {Object} options.charge - Reactive charge object
 * @param {Object} options.dates - Reactive dates object
 * @param {Object} options.formData - Reactive form data
 * @param {Function} options.getRecurrence - Function to get current recurrence
 * @param {Ref} options.originalState - Ref to store original state for comparison
 * @returns {Object} Form state utilities
 */
export function useFixedFeeFormState({
  charge,
  dates,
  formData,
  getRecurrence,
  originalState
}) {
  /**
   * Captures the current form state as the "original" for comparison
   * Call this after loading a charge or initializing a new one
   */
  const captureOriginalState = () => {
    const recurrence = getRecurrence?.() || null;
    originalState.value = cloneDeep(getSimplifiedFormState(charge, dates, formData, recurrence));
  };

  /**
   * Computed property indicating if the form has unsaved changes
   */
  const hasUnsavedChanges = computed(() => {
    if (!originalState.value) {
      // No original state captured yet - check if form has meaningful data
      const hasData = !!(charge.amount || charge.description?.trim() || charge.cm?.matter?.id || charge.matter?.id);
      return hasData;
    }
    const recurrence = getRecurrence?.() || null;
    const currentState = getSimplifiedFormState(charge, dates, formData, recurrence);
    return !isEqual(currentState, originalState.value);
  });

  /**
   * Checks if the form is valid for saving
   * @param {Function} validateFn - Validation function that returns array of errors
   * @returns {Boolean}
   */
  const isFormValid = validateFn => {
    if (!validateFn) return false;
    const errors = validateFn();
    return !errors || errors.length === 0;
  };

  /**
   * Generates dialog configuration based on form state
   * @param {Boolean} isExistingCharge - Whether editing an existing charge
   * @param {Boolean} isValid - Whether the form is valid
   * @param {String} action - Action description (e.g., "selecting another fixed fee", "closing the panel")
   * @returns {Object} Dialog configuration
   */
  const getUnsavedChangesDialogConfig = (isExistingCharge, isValid, action = 'selecting another fixed fee') => {
    // Determine if this is a "closing" action (close button, cancel, etc.)
    const isClosingAction = action.toLowerCase().includes('closing');
    if (isExistingCharge) {
      if (isValid) {
        // Valid form - can save
        if (isClosingAction) {
          // Closing: offer Save or Discard
          return {
            title: 'Unsaved Changes',
            message: `Would you like to save your changes to the selected fixed fee before ${action}?`,
            confirmText: 'Save and Continue',
            toggleText: 'Discard Changes',
            onConfirmAction: 'save',
            proceedOnToggle: true // Discard and proceed to close
          };
        } else {
          // Switching: offer Save or Keep Editing
          return {
            title: 'Unsaved Changes',
            message: `Would you like to save your changes to the selected fixed fee before ${action}?`,
            confirmText: 'Save and Continue',
            toggleText: 'Keep Editing',
            onConfirmAction: 'save',
            proceedOnToggle: false // Stay and keep editing
          };
        }
      } else {
        // Invalid form - can't save
        return {
          title: 'Unsaved Changes',
          message: `Would you like to finish editing the selected fixed fee before ${action}?`,
          confirmText: 'Finish Editing',
          toggleText: 'Discard Changes',
          onConfirmAction: 'stay',
          proceedOnToggle: true // Discard and proceed
        };
      }
    } else {
      // New fixed fee
      if (isValid) {
        if (isClosingAction) {
          // Closing: offer Save or Discard
          return {
            title: 'Unsaved Changes',
            message: `Do you want to save your new fixed fee before ${action}?`,
            confirmText: 'Save and Continue',
            toggleText: 'Discard Changes',
            onConfirmAction: 'save',
            proceedOnToggle: true // Discard and proceed to close
          };
        } else {
          // Switching: offer Save or Keep Editing
          return {
            title: 'Unsaved Changes',
            message: `Do you want to save your new fixed fee before ${action}?`,
            confirmText: 'Save and Continue',
            toggleText: 'Keep Editing',
            onConfirmAction: 'save',
            proceedOnToggle: false // Stay and keep editing
          };
        }
      } else {
        return {
          title: 'Unsaved Changes',
          message: `Would you like to keep editing your new fixed fee before ${action}?`,
          confirmText: 'Keep Editing',
          toggleText: 'Discard Changes',
          onConfirmAction: 'stay',
          proceedOnToggle: true // Discard and proceed
        };
      }
    }
  };

  /**
   * Resets the original state (call after save or when discarding)
   */
  const resetOriginalState = () => {
    originalState.value = null;
  };
  return {
    captureOriginalState,
    hasUnsavedChanges,
    isFormValid,
    getUnsavedChangesDialogConfig,
    resetOriginalState,
    getSimplifiedFormState: () => {
      const recurrence = getRecurrence?.() || null;
      return getSimplifiedFormState(charge, dates, formData, recurrence);
    }
  };
}
export default useFixedFeeFormState;